import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { actionsColDef, type DataGridColDef } from '@procureai/data-grid-ui';
import { DateFormatPreset, formatDate, formatNumber, NumberFormatPreset } from '@procureai/ui-mui';
import { isNil } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getSourceReferenceCode,
  SOURCE_CONTEXT_EXTERNAL_NUMBER,
  SOURCE_CONTEXT_SHOPPING_CART,
} from '../../SavingsWidget.util';
import {
  calculateBaseTotal,
  calculateNegotiatedTotal,
  calculateSavingRatio,
  calculateSavingTotal,
} from '../../utils/savingTotals';
import { type SavingsListQuery } from '@/generated/gql/graphql';
import { NULLISH_CELL_VALUE } from '~/components/DataGridCustom';

import { ActionsCellRenderer } from './cells/ActionsCell';
import { CuriosityCellRenderer } from './cells/CuriosityCell';
import { MultiYearSavingCellRenderer } from './cells/MultiYearSavingCell';
import { SavingTypeCellRenderer } from './cells/SavingTypeCell';
import { SourceReferencesCellRenderer } from './cells/SourceReferencesCell';
import { StatusBadgeCellRenderer } from './cells/StatusBadgeCell';

const decimalValueFormatter = ({ value }: { value: unknown }) =>
  isNil(value) ? NULLISH_CELL_VALUE : formatNumber(NumberFormatPreset.DECIMAL, Number(value));

const percentValueFormatter = ({ value }: { value: unknown }) =>
  isNil(value) ? NULLISH_CELL_VALUE : formatNumber(NumberFormatPreset.PERCENT_DECIMAL, Number(value));

const dateValueFormatter = ({ value }: { value: unknown }) =>
  isNil(value) ? NULLISH_CELL_VALUE : formatDate(DateFormatPreset.TWO_DIGIT, new Date(value as string | number | Date));

type SavingsListRow = SavingsListQuery['savings'][number];

export interface UseSavingsDataGridConfigOptions {
  onEdit?: (id: string) => void;
  onDelete?: (id: string, name: string) => void;
  canModify?: boolean;
}

export interface SavingsDataGridConfig {
  columnDefs: DataGridColDef<SavingsListRow>[];
  defaultColDef: DataGridColDef<SavingsListRow>;
}

export const useSavingsDataGridConfig = (options: UseSavingsDataGridConfigOptions): SavingsDataGridConfig => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'Savings.dataGrid.column' });

  return useMemo<SavingsDataGridConfig>(
    () => ({
      columnDefs: [
        {
          field: 'name',
          headerName: tColumn('name'),
          cellDataType: 'text',
        },
        {
          field: 'currency',
          headerName: tColumn('currency'),
          flex: 0,
          cellDataType: 'text',
        },
        {
          field: 'fkBuyer',
          headerName: tColumn('buyer'),
          flex: 0,
          cellDataType: 'text',
          cellRenderer: CuriosityCellRenderer,
          cellRendererParams: {
            ns: CuriosityNamespaceType.INTERNAL_USER,
          },
        },
        {
          field: 'fkPurchasingGroup',
          headerName: tColumn('purchasingGroup'),
          flex: 0,
          cellDataType: 'text',
          cellRenderer: CuriosityCellRenderer,
          cellRendererParams: {
            ns: CuriosityNamespaceType.PURCHASING_GROUP,
          },
        },
        {
          field: 'fkCommodityGroup',
          headerName: tColumn('commodityGroup'),
          flex: 0,
          cellDataType: 'text',
          cellRenderer: CuriosityCellRenderer,
          cellRendererParams: {
            ns: CuriosityNamespaceType.COMMODITY_GROUP,
          },
        },
        {
          field: 'fkSupplier',
          headerName: tColumn('vendor'),
          flex: 0,
          cellDataType: 'text',
          cellRenderer: CuriosityCellRenderer,
          cellRendererParams: {
            ns: CuriosityNamespaceType.SUPPLIER,
          },
        },
        {
          field: 'fkProcurementOrganization',
          headerName: tColumn('ekOrg'),
          flex: 0,
          cellDataType: 'text',
          cellRenderer: CuriosityCellRenderer,
          cellRendererParams: {
            ns: CuriosityNamespaceType.PROCUREMENT_ORGANIZATION,
          },
        },
        {
          field: 'fkOrganization',
          headerName: tColumn('organization'),
          flex: 0,
          cellDataType: 'text',
          cellRenderer: CuriosityCellRenderer,
          cellRendererParams: {
            ns: CuriosityNamespaceType.ORGANIZATION,
          },
        },
        {
          colId: 'sourceReferences',
          headerName: tColumn('sourceReferences'),
          flex: 0,
          minWidth: 220,
          autoHeight: true,
          sortable: false,
          cellRenderer: SourceReferencesCellRenderer,
        },
        {
          colId: 'purchaseRequisition',
          headerName: tColumn('purchaseRequisition'),
          flex: 0,
          cellDataType: 'text',
          valueGetter: ({ data }) =>
            data ? getSourceReferenceCode(data.sourceReferences, SOURCE_CONTEXT_EXTERNAL_NUMBER) : null,
        },
        {
          colId: 'shoppingCartId',
          headerName: tColumn('shoppingCartId'),
          flex: 0,
          cellDataType: 'text',
          valueGetter: ({ data }) =>
            data ? getSourceReferenceCode(data.sourceReferences, SOURCE_CONTEXT_SHOPPING_CART) : null,
        },
        {
          field: 'fkAccountingArea',
          headerName: tColumn('accountingArea'),
          flex: 0,
          cellDataType: 'text',
          cellRenderer: CuriosityCellRenderer,
          cellRendererParams: {
            ns: CuriosityNamespaceType.ACCOUNTING_AREA,
          },
        },
        {
          field: 'fkUnit',
          headerName: tColumn('unit'),
          flex: 0,
          cellDataType: 'text',
          cellRenderer: CuriosityCellRenderer,
          cellRendererParams: {
            ns: CuriosityNamespaceType.UNIT,
          },
        },
        {
          colId: 'priceOld',
          headerName: tColumn('priceOld'),
          flex: 0,
          cellDataType: 'number',
          type: 'numericColumn',
          valueFormatter: decimalValueFormatter,
          valueGetter: ({ data }) => data?.baselinePrice ?? null,
        },
        {
          colId: 'priceNew',
          headerName: tColumn('priceNew'),
          flex: 0,
          cellDataType: 'number',
          type: 'numericColumn',
          valueFormatter: decimalValueFormatter,
          valueGetter: ({ data }) => data?.newPrice ?? null,
        },
        {
          colId: 'amountPerYear',
          headerName: tColumn('amountPerYear'),
          flex: 0,
          cellDataType: 'number',
          type: 'numericColumn',
          valueFormatter: decimalValueFormatter,
          valueGetter: ({ data }) => data?.baselineQuantity ?? null,
        },
        {
          colId: 'priceUnitOld',
          headerName: tColumn('priceUnitOld'),
          flex: 0,
          cellDataType: 'number',
          type: 'numericColumn',
          valueFormatter: decimalValueFormatter,
          valueGetter: ({ data }) => data?.originalPriceQuantity ?? null,
        },
        {
          colId: 'priceUnitNew',
          headerName: tColumn('priceUnitNew'),
          flex: 0,
          cellDataType: 'number',
          type: 'numericColumn',
          valueFormatter: decimalValueFormatter,
          valueGetter: ({ data }) => data?.newPriceQuantity ?? null,
        },
        {
          colId: 'baseTotal',
          headerName: tColumn('baseTotal'),
          flex: 0,
          cellDataType: 'number',
          type: 'numericColumn',
          valueFormatter: decimalValueFormatter,
          valueGetter: ({ data }) => (data ? calculateBaseTotal(data) : null),
        },
        {
          colId: 'negotiatedTotal',
          headerName: tColumn('negotiatedTotal'),
          flex: 0,
          cellDataType: 'number',
          type: 'numericColumn',
          valueFormatter: decimalValueFormatter,
          valueGetter: ({ data }) => (data ? calculateNegotiatedTotal(data) : null),
        },
        {
          field: 'createdAt',
          headerName: tColumn('createdAt'),
          flex: 0,
          cellDataType: 'dateString',
          valueFormatter: dateValueFormatter,
        },
        {
          field: 'updatedAt',
          headerName: tColumn('updatedAt'),
          flex: 0,
          cellDataType: 'dateString',
          valueFormatter: dateValueFormatter,
        },
        {
          field: 'status',
          headerName: tColumn('status'),
          cellDataType: 'text',
          cellRenderer: StatusBadgeCellRenderer,
        },
        {
          field: 'reductionType',
          headerName: tColumn('reductionType'),
          cellDataType: 'text',
        },
        {
          colId: 'savingTotal',
          headerName: tColumn('savingTotal'),
          cellDataType: 'number',
          type: 'numericColumn',
          valueFormatter: decimalValueFormatter,
          sortable: true,
          valueGetter: ({ data }) => (data ? calculateSavingTotal(data) : null),
        },
        {
          colId: 'savingPercent',
          headerName: tColumn('savingPercent'),
          cellDataType: 'number',
          type: 'numericColumn',
          valueFormatter: percentValueFormatter,
          sortable: true,
          valueGetter: ({ data }) => (data ? calculateSavingRatio(data) : null),
        },
        {
          field: 'validFrom',
          headerName: tColumn('validFrom'),
          cellDataType: 'dateString',
          valueFormatter: dateValueFormatter,
        },
        {
          colId: 'multiYearSaving',
          headerName: tColumn('multiYearSaving'),
          flex: 0,
          cellDataType: 'boolean',
          valueGetter: ({ data }) => data?.multiYearSaving ?? false,
          cellRenderer: MultiYearSavingCellRenderer,
        },
        {
          field: 'savingType',
          headerName: tColumn('savingType'),
          flex: 0,
          cellDataType: 'text',
          cellRenderer: SavingTypeCellRenderer,
        },
        actionsColDef<SavingsListRow>({
          colId: 'actions',
          headerName: t('DataGrid.column.actions'),
          pinned: 'right',
          cellRenderer: ActionsCellRenderer,
          cellRendererParams: {
            onEdit: options.onEdit,
            onDelete: options.onDelete,
            editLabel: t('Savings.actions.edit'),
            deleteLabel: t('Savings.actions.delete'),
            canModify: options.canModify,
          },
        }),
      ],
      defaultColDef: {
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 100,
      },
    }),
    [options.onEdit, options.onDelete, options.canModify, t, tColumn],
  );
};
